import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { FilterSpec, Pageable, WrapperPageableResponse, WrapperResponse } from './service-common';
import { PublicSubscriptionCheckoutRequest, WrapperResponseSubscriptionDTO } from '../core/models';

export type SaleLinkStatus = 'ACTIVE' | 'PAID' | 'CANCELLED' | 'EXPIRED';
export type SaleLinkType = 'SINGLE' | 'REUSABLE' | 'SUBSCRIPTION';
export type BillingFrequency = 'ONCE' | 'DAILY' | 'WEEKLY' | 'BIWEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'SEMIANNUALLY' | 'ANNUALLY';

export interface SaleLinkDTO {
  id: number;
  number?: string;
  status?: SaleLinkStatus;
  customerId?: number;
  customerName?: string;
  customerTaxId?: string;
  customerEmail?: string;
  productId?: number;
  productName?: string;
  productSku?: string;
  quantity?: number;
  unitPrice?: number;
  lines?: SaleLinkLineDTO[];
  type?: SaleLinkType;
  frequency?: BillingFrequency;
  collectionMethod?: string;
  expiresAt?: string;
  notes?: string;
  comercialMetadata?: Record<string, string>;
  createdAt?: string;
  currentTotal?: number;
  currencyCode?: string;
}

export interface SaleLinkLineDTO {
  id?: number;
  productId?: number;
  productName?: string;
  productSku?: string;
  quantity?: number;
  unitPrice?: number;
  description?: string;
}

export interface PublicSaleLinkLineDTO {
  name?: string;
  quantity?: number;
  unitPrice?: number;
  subtotal?: number;
  total?: number;
}

export interface PublicSaleLinkDTO {
  id: number;
  number?: string;
  status?: SaleLinkStatus;
  type?: SaleLinkType;
  frequency?: BillingFrequency;
  provider?: string;
  concept?: string;
  quantity?: number;
  unitPrice?: number;
  lines?: PublicSaleLinkLineDTO[];
  subtotal?: number;
  tax?: number;
  total?: number;
  currencyCode?: string;
  expiresAt?: string;
  notes?: string;
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  companyName?: string;
  companyLogoUrl?: string;
  paymentUrl?: string;
}

export interface CreateSaleLinkCustomerRequest {
  type: 'PERSON' | 'COMPANY';
  taxId: string;
  legalName: string;
  comercialName: string;
  email: string;
  phone: string;
  countryId: number;
}

export interface CreateSaleLinkProductRequest {
  name: string;
  sku: string;
  unitPrice: number;
  currencyId: number;
  measureUnitId: number;
  taxCodeId: number;
  taxInclusive?: boolean;
}

export interface CreateSaleLinkLineRequest {
  productId?: number;
  product?: CreateSaleLinkProductRequest;
  quantity?: number;
  unitPrice?: number;
  description?: string;
}

export interface CreateSaleLinkRequest {
  customerId?: number;
  customer?: CreateSaleLinkCustomerRequest;
  lines?: CreateSaleLinkLineRequest[];
  expiresAt?: string;
  notes?: string;
  comercialMetadata?: Record<string, string>;
  type?: SaleLinkType;
  frequency?: BillingFrequency;
  collectionMethod?: string;
}

export type SaleLinkFilterSpec = FilterSpec;

@Injectable({ providedIn: 'root' })
export class SaleLinkService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  list(spec: SaleLinkFilterSpec = {}, pageable: Pageable = { page: 0, size: 20 }): Observable<WrapperPageableResponse<SaleLinkDTO>> {
    return this.http.get<WrapperPageableResponse<SaleLinkDTO>>(`${this.apiUrl}/sale-links/list`, {
      params: this.params(spec, pageable)
    });
  }

  getById(id: number): Observable<WrapperResponse<SaleLinkDTO>> {
    return this.http.get<WrapperResponse<SaleLinkDTO>>(`${this.apiUrl}/sale-links/${id}`);
  }

  create(request: CreateSaleLinkRequest): Observable<WrapperResponse<SaleLinkDTO>> {
    return this.http.post<WrapperResponse<SaleLinkDTO>>(`${this.apiUrl}/sale-links`, request);
  }

  update(id: number, request: CreateSaleLinkRequest): Observable<WrapperResponse<SaleLinkDTO>> {
    return this.http.put<WrapperResponse<SaleLinkDTO>>(`${this.apiUrl}/sale-links/${id}`, request);
  }

  cancel(id: number): Observable<WrapperResponse<SaleLinkDTO>> {
    return this.http.post<WrapperResponse<SaleLinkDTO>>(`${this.apiUrl}/sale-links/${id}/cancel`, {});
  }

  publicHash(id: number): Observable<WrapperResponse<string>> {
    return this.http.get<WrapperResponse<string>>(`${this.apiUrl}/sale-links/${id}/public-hash`);
  }

  send(id: number, email: string): Observable<WrapperResponse<void>> {
    return this.http.post<WrapperResponse<void>>(`${this.apiUrl}/sale-links/${id}/send`, { email });
  }

  getPublic(id: number, hash: string): Observable<WrapperResponse<PublicSaleLinkDTO>> {
    const params = new HttpParams().set('hash', hash);
    return this.http.get<WrapperResponse<PublicSaleLinkDTO>>(`${this.apiUrl}/public/sale-links/${id}`, { params });
  }

  payPublic(id: number, hash: string): Observable<WrapperResponse<PublicSaleLinkDTO>> {
    const params = new HttpParams().set('hash', hash);
    return this.http.post<WrapperResponse<PublicSaleLinkDTO>>(`${this.apiUrl}/public/sale-links/${id}/pay`, {}, { params });
  }

  checkoutSubscription(
    id: number,
    hash: string,
    request: PublicSubscriptionCheckoutRequest
  ): Observable<WrapperResponseSubscriptionDTO> {
    const params = new HttpParams().set('hash', hash);
    return this.http.post<WrapperResponseSubscriptionDTO>(
      `${this.apiUrl}/public/sale-links/${id}/subscription/checkout`,
      request,
      { params }
    );
  }

  publicPageUrl(id: number, hash: string, type?: SaleLinkType): string {
    const page = type === 'SUBSCRIPTION' ? 'subscription' : 'receipt';
    return `${window.location.origin}/public/sale-links/${id}/${page}?hash=${encodeURIComponent(hash)}`;
  }

  private params(spec: SaleLinkFilterSpec, pageable: Pageable): HttpParams {
    let params = new HttpParams();
    Object.entries(spec).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.append(key, String(value));
      }
    });
    params = params.append('page', String(pageable.page));
    params = params.append('size', String(pageable.size));
    pageable.sort?.forEach((sort) => {
      params = params.append('sort', sort);
    });
    return params;
  }
}
